import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth.ts';

export const api: AxiosInstance = axios.create({ baseURL: '/api', withCredentials: true });

api.interceptors.request.use((cfg) => {
  const t = useAuthStore.getState().accessToken;
  if (t && cfg.headers) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Classify how a session-recovery attempt ended, so a transient network/5xx
// blip never logs the user out. Only a genuine 401 (revoked/expired refresh
// token, with no ambient SSO fallback) is treated as a dead session.
type Recovery = 'recovered' | 'dead' | 'transient';

async function recoverSession(): Promise<Recovery> {
  try {
    await useAuthStore.getState().refresh();
    return 'recovered';
  } catch (e) {
    const status = (e as { response?: { status?: number } })?.response?.status;
    if (status === 401) {
      // Refresh token genuinely revoked/expired — try the ambient SSO cookie
      // (Office shared session) before giving up on the session.
      const ok = await useAuthStore.getState().ssoBootstrap(true);
      return ok ? 'recovered' : 'dead';
    }
    // Network error / 5xx — do NOT log out; the session is almost certainly fine.
    return 'transient';
  }
}

let refreshing: Promise<Recovery> | null = null;
api.interceptors.response.use(
  (r) => r,
  async (err: { response?: { status?: number }; config?: InternalAxiosRequestConfig & { _retry?: boolean } }) => {
    const status = err.response?.status;
    const original = err.config;
    if (status === 401 && original && !original._retry) {
      original._retry = true;
      refreshing ??= recoverSession().finally(() => { refreshing = null; });
      const outcome = await refreshing;
      if (outcome === 'recovered') return api(original);
      if (outcome === 'dead') {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
      // 'transient' → fall through and reject; the session stays signed in.
    }
    throw err;
  },
);
